/**
 * 시료 목 데이터. 출처: 와이어프레임 `SMP`(2398행) · `SAMPLES` 9챔버(1068행).
 *
 * 🔴 `patient_id` 와 `sample_id` 는 **`view` 권한 없는 역할(`service`)에게 마스킹 대상**이다.
 *    fixture 는 원값을 그대로 담는다 — 가리는 일은 화면에서 `maskPii()` 가 한다.
 *    (계약에 마스킹 필드가 따로 없다. 서버는 값을 주고 노출만 역할로 가른다.)
 */
import type { Sample } from '@ctcells/contract'

/*
 * 🔴 **새 칸 셋(2026-09-26 · 서버 `app/views/samples.py` `_dto` 대조)**
 *   - `created_at` — 등록 시각. **서버가 늘 싣는다**(실측 349/349). 그래서 fixture 도 전 행에 둔다
 *   - `assigned_well` — 배치 생성 때 배정된 웰(불변). `current_position`(지금 자리)과 **다른 축**이라
 *     같은 웰 이름 어휘를 쓴다(`A1` …) — 두 값이 같아도 달라도 결함이 아니다(`DM-REL-06`)
 *   - `sample_ref` — 레시피 묶음 자리표(조회 때 파생). 레시피 fixture `sample_groups` 의 `S1`~`S4` · `REF`
 *   ⚠ 실물은 둘 다 **0/349** 다(배정 없이 뜬 배치뿐). `exclude_none` 이라 **키가 없다** — 그래서
 *     배정 없는 행(S-1043 · 미배정 · 완료 · 폐기)에는 키를 **빼 둔다**. 한 꼴만 두면 빈 칸 경계가 목을 통과한다
 */

/** 9챔버에 들어가는 시료 ID. 8 시료 + 대조(Reference) 1 */
export const CHAMBER_SAMPLE_IDS = [
  'S-1041',
  'S-1042',
  'S-1043',
  'S-1044',
  'S-1045',
  'S-1046',
  'S-1047',
  'S-1048',
  'Ref',
] as const

export const REFERENCE_SAMPLE_ID = 'Ref'

/**
 * 🔴 **도는 작업의 시료는 그 작업의 «진행»과 맞물려야 한다**(2026-09-21 실제 사고).
 *
 * 종전에 이 아홉 건이 `stage: 'staining'` 이었다. 그런데 demo 의 도는 작업(`SCENARIO_RUNNING`)은
 * **원심 24 %** 다(`segments([0.24, 0, 0])` · `MODULES_RUNNING` 의 `cent` 만 `RUNNING`).
 * 두 축이 갈리자 시료 흐름 격자가 **어셈블·염색을 「지남」으로 읽고 그 위에 「성공」을 얹었다** —
 * 「원심 진행 중인데 염색 성공」이라는 있을 수 없는 화면이 demo 에서만 떴다.
 *
 * ⇒ **`registered` 다.** 원심이 «아직 돌고 있으므로» `centrifuged` 표지는 찍히지 않았다.
 * ⚠ **시료마다 단계를 다르게 두지 않는다.** 서버는 단계를 배치 단위로 올린다
 *   (`record_batch_wide` · eric 결정 2026-09-14) — 같은 작업의 9건은 언제나 같은 칸까지다.
 *   회차별로 갈라 두면 **fixture 가 실물에 없는 모양**이 되고, 그 모양에서만 도는 코드가 생긴다.
 * ⚠ `current_module` 은 **demo 모듈 ID 꼴**(`cent`)이다 — 실물 꼴(`centrifuge-1`)을 쓰면 demo 의
 *   모듈 카드·챔버 표가 한 대도 못 맞춘다(`modules.ts` 파일 주석 참조).
 */
const inBatch = (
  sample_id: string,
  patient_id: string,
  position: string,
  extra: Partial<Sample> = {},
): Sample => ({
  sample_id,
  patient_id,
  batch_id: 'B-260911-02',
  created_at: '2026-09-11T00:12:00+00:00',
  sample_type: '환자 혈액',
  rbc_concentration: 4.7,
  blood_draw_date: '2026-09-10',
  cancer_type: '폐암',
  cancer_stage: 'IIIA',
  stage: 'registered',
  status: 'in_process',
  current_module: 'cent',
  current_position: position,
  version: 1,
  ...extra,
})

export const SAMPLES: Sample[] = [
  inBatch('S-1041', 'P-2201', 'A1', { assigned_well: 'A1', sample_ref: 'S1' }),
  inBatch('S-1042', 'P-2202', 'A2', {
    rbc_concentration: 4.9,
    cancer_stage: 'IIB',
    assigned_well: 'A2',
    sample_ref: 'S1',
  }),
  // 메타 일부가 비어 있는 행 — 「빈 값」 경계를 화면에서 확인하라고 남긴다
  {
    sample_id: 'S-1043',
    patient_id: 'P-2203',
    batch_id: 'B-260911-02',
    sample_type: '환자 혈액',
    stage: 'registered',
    status: 'in_process',
    current_module: 'cent',
    current_position: 'B1',
    created_at: '2026-09-11T00:11:30+00:00',
    version: 2,
  },
  inBatch('S-1044', 'P-2204', 'B2', {
    rbc_concentration: 5.1,
    blood_draw_date: '2026-09-09',
    cancer_type: '대장암',
    cancer_stage: 'II',
    assigned_well: 'B2',
    sample_ref: 'S2',
  }),
  inBatch('S-1045', 'P-2205', 'A3', {
    rbc_concentration: 4.8,
    cancer_stage: 'IIA',
    assigned_well: 'A3',
    sample_ref: 'S3',
  }),
  inBatch('S-1046', 'P-2206', 'B3', {
    rbc_concentration: 4.5,
    cancer_type: '위암',
    cancer_stage: 'I',
    assigned_well: 'B3',
    sample_ref: 'S3',
  }),
  inBatch('S-1047', 'P-2207', 'C1', {
    rbc_concentration: 5.2,
    cancer_stage: 'IIIB',
    assigned_well: 'C1',
    sample_ref: 'S4',
  }),
  inBatch('S-1048', 'P-2208', 'C2', {
    rbc_concentration: 4.6,
    cancer_stage: 'IIA',
    // 묶음을 못 이은 행 — 자리는 있는데 `sample_ref` 가 null 인 꼴(묶음이 표지를 안 적었다)
    assigned_well: 'C2',
  }),
  /*
   * 대조 시료 — 환자 식별자가 없다. 화면은 「—」 로 떨어뜨린다.
   * 🔴 **`sample_type` 이 정확히 `'Reference'` 여야 한다**(2026-09-21 실물 화면에서 잡았다).
   *    화면의 대조 판정이 그 한 값이고(`flowSamples` · `monitoringModel`) 종전 값
   *    `'Reference · 대조'` 는 그 판정을 **통과하지 못해** demo 에서 이 시료가 환자 1 자리에
   *    들어가 앉고 대조 묶음이 「배정 정보 없음」이 됐다. fixture 는 서버 DTO 모양을 따른다.
   */
  {
    sample_id: REFERENCE_SAMPLE_ID,
    batch_id: 'B-260911-02',
    sample_type: 'Reference',
    stage: 'registered',
    status: 'in_process',
    current_module: 'cent',
    current_position: 'C3',
    assigned_well: 'C3',
    sample_ref: 'REF',
    created_at: '2026-09-11T00:12:00+00:00',
    version: 1,
  },
  /*
   * 종착 — 완료.
   * 🔴 **`current_module` 을 «남겨 둔다».** 서버가 종착에서 그 칸을 비우지 않기 때문이다
   *    (BR-52 · 백엔드 자신이 「`current_module` 을 비우는 자리가 어디에도 없어」라고 적어 뒀다 ·
   *    `tray_confirm.py` `_batch_of_module`). 실측으로 `staining-1` 에 46건이 걸렸는데 그중
   *    **37건이 `completed`** 였다.
   * ⚠ 이 한 줄이 **「끝난 시료는 세지 않는다」를 demo 에서 실제로 시험한다**(`samplesAt`).
   *   비워 두면 fixture 가 실물보다 순해져서 그 거르기가 목을 그냥 통과한다.
   */
  {
    sample_id: 'S-0e204884',
    patient_id: 'P-2',
    batch_id: 'B-260910-03',
    sample_type: '환자 혈액',
    rbc_concentration: 4.4,
    blood_draw_date: '2026-09-05',
    cancer_type: '유방암',
    cancer_stage: 'I',
    stage: 'completed',
    status: 'completed',
    current_module: 'stain',
    created_at: '2026-09-10T01:05:00+00:00',
    version: 3,
  },
  // 미배정 — 등록만 된 행 3건(작업 만들기 화면의 선택 후보)
  {
    sample_id: 'S-1051',
    /*
     * 🔴 **정정 2026-09-20 — 서버는 미배정 행의 `batch_id` «키 자체를 뺀다».**
     *    `app/views/samples.py` 의 목록·단건·등록이 전부 `model_dump(exclude_none=True)` 다
     *    (실측: `GET /samples` 에서 미배정 행은 `sample_id`·`stage`·`status`·`version` 넷뿐).
     *    종전 주석은 「키를 주고 값만 null」이라 적었는데 **틀렸다.**
     * ⚠ 그래서 **두 꼴을 다 둔다** — 여기 한 건은 키를 «빼고»(서버 그대로), 아래 둘은
     *   `null` 로 둔다. 판정은 어느 쪽이든 **`== null`** 이어야 성립하고, 한 꼴만 두면
     *   다른 꼴에서 깨지는 코드가 목을 통과한다.
     */
    patient_id: 'P-2301',
    sample_type: '환자 혈액',
    rbc_concentration: 4.6,
    blood_draw_date: '2026-09-15',
    cancer_type: '폐암',
    cancer_stage: 'IIA',
    stage: 'registered',
    status: 'in_process',
    created_at: '2026-09-15T05:40:00+00:00',
    version: 0,
  },
  {
    sample_id: 'S-1052',
    // ⚠ `null` 꼴. 🔴 **판정은 `== null` 하나다** — `=== null` 로 보면 위 S-1051 에서 깨진다
    batch_id: null,
    patient_id: 'P-2302',
    sample_type: '환자 혈액',
    blood_draw_date: '2026-09-15',
    cancer_type: '위암',
    stage: 'registered',
    status: 'in_process',
    created_at: '2026-09-15T05:41:00+00:00',
    version: 0,
  },
  {
    sample_id: 'S-1053',
    // ⚠ `null` 꼴. 🔴 **판정은 `== null` 하나다** — `=== null` 로 보면 위 S-1051 에서 깨진다
    batch_id: null,
    patient_id: 'P-2303',
    sample_type: '환자 혈액',
    rbc_concentration: 5.0,
    blood_draw_date: '2026-09-16',
    cancer_type: '대장암',
    cancer_stage: 'III',
    stage: 'registered',
    status: 'in_process',
    created_at: '2026-09-16T02:20:00+00:00',
    version: 0,
  },
  {
    sample_id: 'S-ee2201bf',
    patient_id: 'P-1',
    batch_id: 'B-260911-06',
    sample_type: '전임상 모델',
    blood_draw_date: '2026-09-08',
    stage: 'registered',
    status: 'in_process',
    created_at: '2026-09-08T07:00:00+00:00',
    version: 0,
  },
  /*
   * 폐기 — `status=discarded` 면 `discard_reason` 이 필수다(계약 if/then).
   * 🔴 위 「완료」 행과 같은 까닭으로 `current_module` 이 **남아 있다** — 폐기도 그 칸을 안 비운다
   *    (실측: `centrifuge-1` 에 `discarded` 9건). 모듈 축만으로 세면 이 행까지 들어간다.
   */
  {
    sample_id: 'cmp-f8aaac-s01',
    batch_id: 'B-260910-02',
    sample_type: '검증용',
    stage: 'assembled',
    status: 'discarded',
    discard_reason: '어셈블러 교착(assembled 미발행)으로 진행 불가',
    current_module: 'asm',
    created_at: '2026-09-10T00:30:00+00:00',
    version: 2,
  },
]

/** 시료 ID 로 찾기. 화면이 챔버 표에서 요약을 펼칠 때 쓴다 */
export function findSample(sampleId: string): Sample | undefined {
  return SAMPLES.find((s) => s.sample_id === sampleId)
}
